import { useScrollAnimation } from '../hooks/useScrollAnimation';

const steps = [
  {
    number: '01',
    icon: 'ri-plug-line',
    highlight: '5 minutos',
    highlightColor: 'bg-coral/10 text-coral',
    title: 'Conecte',
    text: 'Conecte seu número de WhatsApp à plataforma Automat. Leva 5 minutos, sem conhecimento técnico.',
    delay: 0,
  },
  {
    number: '02',
    icon: 'ri-brain-line',
    highlight: '5–10 dias',
    highlightColor: 'bg-navy/10 text-navy',
    title: 'Treine',
    text: 'Nosso time treina a IA com seu catálogo, tabela de preços, FAQs e regras de negócio. Te ajudamos no onboarding completo.',
    delay: 150,
  },
  {
    number: '03',
    icon: 'ri-rocket-2-line',
    highlight: '24/7',
    highlightColor: 'bg-green-100 text-green-700',
    title: 'Venda',
    text: 'O agente começa a atender seus clientes imediatamente. Você acompanha tudo em tempo real pelo CRM e dashboard.',
    delay: 300,
  },
];

interface HowItWorksProps {
  onCtaClick: () => void;
}

export default function HowItWorks({ onCtaClick }: HowItWorksProps) {
  const { ref, visible } = useScrollAnimation(0.1);

  return (
    <section id="how-it-works" className="bg-white py-24 lg:py-32 px-6">
      <div className="max-w-6xl mx-auto" ref={ref}>
        {/* Badge */}
        <div
          className={`flex justify-center mb-5 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="font-b2bbody text-xs font-semibold tracking-widest uppercase border border-coral/30 text-coral rounded-full px-4 py-1.5">
            Como Funciona
          </span>
        </div>

        {/* Title */}
        <div
          className={`text-center mb-20 transition-all duration-700 delay-75 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="font-b2b text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-navy leading-tight">
            3 passos para seu agente de IA{' '}
            <span className="text-coral">começar a vender</span>
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Connector line — desktop */}
          <div
            className={`hidden lg:block absolute top-10 left-[calc(16.666%+20px)] right-[calc(16.666%+20px)] h-[2px] transition-all duration-1000 delay-200 ${
              visible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
            style={{
              background: 'linear-gradient(90deg, #E94560 0%, #112448 50%, #10B981 100%)',
              transformOrigin: 'left center',
            }}
          />

          {/* Steps grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-8">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`relative flex flex-col lg:items-center transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: visible ? `${step.delay + 200}ms` : '0ms' }}
              >
                {/* Mobile vertical line */}
                {step.number !== '03' && (
                  <div
                    className="lg:hidden absolute left-9 top-20 w-[2px] h-14 bg-gray-200"
                    style={{ zIndex: 0 }}
                  />
                )}

                {/* Circle + Content: horizontal on mobile, vertical on desktop */}
                <div className="flex lg:flex-col items-start lg:items-center gap-5 lg:gap-0 py-6 lg:py-0">
                  {/* Circle */}
                  <div className="flex-shrink-0 relative z-10">
                    <div
                      className="w-[4.5rem] h-[4.5rem] rounded-full flex flex-col items-center justify-center border-4 border-white"
                      style={{
                        background: step.number === '01'
                          ? 'linear-gradient(135deg, #E94560, #C93050)'
                          : step.number === '02'
                          ? 'linear-gradient(135deg, #0A1628, #162D5A)'
                          : 'linear-gradient(135deg, #059669, #10B981)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      }}
                    >
                      <i className={`${step.icon} text-white text-xl`} />
                      <span className="text-white/70 text-[9px] font-b2b font-bold leading-none mt-0.5">
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Text */}
                  <div className="lg:mt-7 lg:text-center">
                    {/* Highlight badge */}
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-b2b font-bold px-3 py-1 rounded-full mb-3 ${step.highlightColor}`}
                    >
                      <i className="ri-time-line text-xs" />
                      {step.highlight}
                    </span>

                    <h3 className="font-b2b text-xl font-bold text-navy mb-2">
                      {step.title}
                    </h3>
                    <p className="font-b2bbody text-gray-500 text-sm leading-relaxed max-w-xs">
                      {step.text}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div
          className={`flex justify-center mt-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: visible ? '600ms' : '0ms' }}
        >
          <button
            onClick={onCtaClick}
            className="whitespace-nowrap font-b2bbody font-bold text-base px-8 py-4 rounded-xl text-white cursor-pointer transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #E94560, #C93050)' }}
          >
            <i className="ri-whatsapp-line text-lg" />
            Quero Ver Meu Agente em Ação
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}